// frontend/src/hooks/useCategories.js

import { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
import { CACHE_DURATIONS } from '../config/cacheConfig';

// ==========================================
// CACHE CONFIGURATION
// ==========================================

const CACHE_DURATION = CACHE_DURATIONS.CATEGORIES;

const categoriesCache = {
  data: null,
  timestamp: 0
};

/**
 * Hook to load categories from API - WITH CACHING
 *
 * Usage:
 * const { categories, loading, error, refetch } = useCategories();
 *
 * Returns:
 * - categories: Array of category objects
 * - loading: Boolean loading state
 * - error: Error message if failed
 * - refetch: Function to force reload categories
 */
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && categoriesCache.data) {
      const cacheAge = Date.now() - categoriesCache.timestamp;
      if (cacheAge < CACHE_DURATION) {
        // console.log('📦 Using cached categories');
        setCategories(categoriesCache.data);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // STATIC MODE: load from local JSON file
      const response = await fetch('/data/categories.json');

      if (!response.ok) {
        throw new Error(`Failed to load categories: ${response.status}`);
      }

      const data = await response.json();
      const categoriesData = Array.isArray(data) ? data : [];
      // Sort by display_order
      categoriesData.sort((a, b) => (a.display_order || 99) - (b.display_order || 99));
      // Update cache
      categoriesCache.data = categoriesData;
      categoriesCache.timestamp = Date.now();

      //  Fetched fresh categories');
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err.message);

      // Fallback categories if API fails
        setCategories([
          { id: 'CODE_GENERATION', name: 'Code Generation', display_order: 1 },
          { id: 'CHAT', name: 'Chat', display_order: 2 },
          { id: 'IMAGE_GENERATION', name: 'Image Generation', display_order: 3 }
        ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    refetch: () => fetchCategories(true)
  };
};

/**
 * Hook to get a single category by ID
 */
export const useCategory = (categoryId) => {
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!categoryId) {
      setLoading(false);
      return;
    }

    const fetchCategory = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/categories/${categoryId}`);

        if (!response.ok) {
          throw new Error('Category not found');
        }

        const data = await response.json();
        setCategory(data);
      } catch (err) {
        console.error('Failed to load category:', err);
        setError(err.message);
        setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [categoryId]);

  return { category, loading, error };
};

/**
 * Hook for admin category management
 */
export const useCategoryManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear cache when categories are modified
  const clearCache = () => {
    categoriesCache.data = null;
    categoriesCache.timestamp = 0;
  };

  const createCategory = async (categoryData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create category');
      }

      const data = await response.json();
      clearCache();
      return { success: true, data };
    } catch (err) {
      console.error('Failed to create category:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (categoryId, updates) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/categories/${encodeURIComponent(categoryId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update category');
      }

      const data = await response.json();
      clearCache();
      return { success: true, data };
    } catch (err) {
      console.error('Failed to update category:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/categories/${encodeURIComponent(categoryId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete category');
      }

      clearCache();
      return { success: true };
    } catch (err) {
      console.error('Failed to delete category:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    createCategory,
    updateCategory,
    deleteCategory,
    loading,
    error
  };
};

// Export cache clear function for external use
export const clearCategoriesCache = () => {
  categoriesCache.data = null;
  categoriesCache.timestamp = 0;
};